import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonLink } from '../button-link/ButtonLink';
import styles from './ExpandableText.module.scss';

const DEFAULT_MAX_LINES = 4;

export interface ExpandableTextProps {
  /** Plain-text content to display. */
  children: string;
  /** Maximum number of visible lines before the text is expanded. */
  maxLines?: number;
  /** Class forwarded to the wrapping paragraph. */
  className?: string;
}

export const ExpandableText = ({
  children,
  maxLines = DEFAULT_MAX_LINES,
  className,
}: ExpandableTextProps) => {
  const { t } = useTranslation('common');
  const [expandedText, setExpandedText] = useState<string>();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const isExpanded = expandedText === children;

  useLayoutEffect(() => {
    const text = textRef.current;

    if (!children || !text || isExpanded) {
      return undefined;
    }

    const updateOverflow = () => {
      setIsOverflowing(maxLines > 0 && text.scrollHeight > text.clientHeight);
    };

    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(text);

    return () => observer.disconnect();
  }, [children, isExpanded, maxLines]);

  const handleToggle = () => {
    setExpandedText(currentText =>
      currentText === children ? undefined : children
    );
  };

  return (
    <p className={className}>
      <span
        ref={textRef}
        className={clsx(styles.text, {
          [styles.collapsed]: !isExpanded,
        })}
        style={isExpanded ? undefined : { WebkitLineClamp: maxLines }}
      >
        {children}
      </span>
      {isOverflowing && (
        <ButtonLink
          className={styles.toggle}
          label={isExpanded ? t('common:readLess') : t('common:readMore')}
          onClick={handleToggle}
        />
      )}
    </p>
  );
};
